import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialRemittanceUsageDTO } from "../../../types/FinancialDTO";
import { downloadBlob } from "../../../utils/financialFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import "./RemittanceModal.scss";

interface Props {
	open: boolean;
	idContas: number[];
	names: Record<number, string>;
	onClose: () => void;
	onGenerated: () => void;
}

const fileNameFrom = (header: string | undefined, sequence: number) => {
	const match = header?.match(/filename="?([^"]+)"?/);
	return match ? match[1] : `CI400_001_${String(sequence).padStart(7, '0')}.REM`;
};

const errorMessage = async (error: any) => {
	const data = error?.response?.data;
	if (data instanceof Blob) {
		return await data.text();
	}
	return typeof data === 'string' ? data : undefined;
};

const RemittanceModal: FC<Props> = ({ open, idContas, names, onClose, onGenerated }) => {
	const [sequence, setSequence] = useState('');
	const [usage, setUsage] = useState<FinancialRemittanceUsageDTO[]>([]);
	const [generating, setGenerating] = useState(false);
	const { showAlert, showError } = useAlert();
	const service = new FinancialService();

	useEffect(() => {
		if (!open) {
			return;
		}
		const load = async () => {
			const [next, used] = await Promise.all([service.getNextSequence(), service.checkRemittances(idContas)]);
			setSequence(next ? String(next.data.sequence) : '');
			setUsage(used?.data || []);
		};
		load();
	}, [open]);

	const generate = async () => {
		const value = Number(sequence);
		setGenerating(true);
		try {
			const response = await service.generateRemittance(value, idContas);
			downloadBlob(response.data, fileNameFrom(response.headers['content-disposition'], value));
			showAlert(`Remessa ${value} gerada com ${idContas.length} boleto(s)`);
			onGenerated();
		} catch (error) {
			showError(await errorMessage(error));
		}
		setGenerating(false);
	};

	const valid = /^\d{1,7}$/.test(sequence) && Number(sequence) > 0;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Gerar arquivo .REM</DialogTitle>
			<DialogContent className="remittance-modal">
				<TextField label="Sequência" value={sequence} onChange={(e) => setSequence(e.target.value.replace(/\D/g, ''))}
					helperText="Próximo número após a última remessa registrada. Ajuste se um arquivo foi gerado por fora."
					fullWidth margin="dense"/>
				<span>{`${idContas.length} fatura(s) selecionada(s)`}</span>
				{!!usage.length &&
					<Alert severity="warning">
						<div>Faturas que já estão em remessa anterior:</div>
						{usage.map(item => (
							<div key={item.idConta}>{`${names[item.idConta] || item.idConta}: remessa nº ${item.sequences.join(', ')}`}</div>
						))}
					</Alert>
				}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				<Button variant="contained" disabled={!valid || generating} onClick={generate}
					startIcon={generating ? <CircularProgress size={16}/> : undefined}>
					Gerar e baixar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default RemittanceModal
